/** Raw cookie + IndexedDB + native backup for clientId (survives some localStorage wipes). */

import { setNativeClientId } from './clientIdNative';

export const CLIENT_ID_COOKIE_NAME = 'km220ClientId';
export const CLIENT_ID_IDB_NAME = 'km220';
export const CLIENT_ID_IDB_STORE = 'kv';
export const CLIENT_ID_IDB_KEY = 'clientId';

/** Safari caps document.cookie lifetime; 400 days is the practical max. */
const COOKIE_MAX_AGE_SECONDS = 400 * 24 * 60 * 60;

export function setClientIdCookie(value) {
  if (typeof document === 'undefined' || !value) {
    return;
  }
  document.cookie =
    `${CLIENT_ID_COOKIE_NAME}=${encodeURIComponent(value)}` + `;max-age=${COOKIE_MAX_AGE_SECONDS};path=/;SameSite=Lax`;
}

export function getClientIdCookie() {
  if (typeof document === 'undefined') {
    return null;
  }
  const parts = `; ${document.cookie}`.split(`; ${CLIENT_ID_COOKIE_NAME}=`);
  if (parts.length < 2) {
    return null;
  }
  const raw = parts.pop().split(';').shift();
  if (!raw) {
    return null;
  }
  try {
    return decodeURIComponent(raw);
  } catch (_e) {
    return raw;
  }
}

function openClientIdDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB unavailable'));
      return;
    }
    const request = indexedDB.open(CLIENT_ID_IDB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(CLIENT_ID_IDB_STORE)) {
        db.createObjectStore(CLIENT_ID_IDB_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('indexedDB open failed'));
  });
}

export async function setClientIdIndexedDb(value) {
  if (!value) {
    return;
  }
  const db = await openClientIdDb();
  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(CLIENT_ID_IDB_STORE, 'readwrite');
      tx.objectStore(CLIENT_ID_IDB_STORE).put(value, CLIENT_ID_IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error || new Error('indexedDB put failed'));
    });
  } finally {
    db.close();
  }
}

export async function getClientIdIndexedDb() {
  const db = await openClientIdDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(CLIENT_ID_IDB_STORE, 'readonly');
      const request = tx.objectStore(CLIENT_ID_IDB_STORE).get(CLIENT_ID_IDB_KEY);
      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => reject(request.error || new Error('indexedDB get failed'));
    });
  } finally {
    db.close();
  }
}

/** Write backups without throwing; IndexedDB / native are best-effort. */
export function persistClientIdBackups(value) {
  if (!value) {
    return;
  }
  try {
    setClientIdCookie(value);
  } catch (_e) {
    // ignore cookie write failures
  }
  setClientIdIndexedDb(value).catch(() => {
    // ignore IndexedDB write failures
  });
  try {
    setNativeClientId(value);
  } catch (_e) {
    // ignore native write failures
  }
}
